import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Button,
  Grid,
  Link,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { Box, Container } from '@mui/system';

import DynamicBreadcrumbs from '@/components/nav/Breadcrumb';
import ResourcesRightSidebar from '@/components/nav/ResourcesRightSidebar';
import AppLayout from '@/layouts/AppLayout';
import { Meta } from '@/layouts/Meta';

const Form1098DueDates = () => {
  return (
    <AppLayout
      meta={
        <Meta
          title="Form 1098 series deadlines for Tax Year 2025 |TaxZerone"
          description="Know the IRS deadlines for the Form 1098 series for Tax Year 2025. Meet each due date accurately and avoid late filing penalties with TaxZerone."
          keywords=""
          canonical="resources/information-returns/form-1098-series-due-dates/"
          ogType="article"
          jsonLdPricing="2.49"
          jsonLdAggregateRatingValue="5.00"
          jsonLdAggregateRatingCount="57448"
        />
      }
    >
      <Container maxWidth="lg" sx={{ textAlign: 'center' }}>
        <Typography
          variant="h1"
          sx={{
            fontSize: { lg: 40, md: 36, sm: 32, xs: 24 },
            fontWeight: 600,

            mt: 3,
            textAlign: 'center',
          }}
        >
          <span className="highlight-container">
            <span className="highlight">Form 1098</span>
          </span>{' '}
          Series Due Dates
        </Typography>
        <Typography
          variant="body1"
          sx={{
            fontSize: { lg: 20, md: 18, sm: 16, xs: 16 },
            fontWeight: 400,
            mt: 1,
            mb: 2,
          }}
        >
          Learn about the Due dates of Form 1098, 1098-T, and 1098-E, and Act
          fast!
        </Typography>
        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <Button
            variant="contained"
            className="btn-primary medium"
            sx={{
              fontSize: {
                xs: '16px !important',
                sm: '18px !important',
                lg: '24px !important',
              },
              transition: 'all 0.3s ease 0s',
              '&:hover, &:focus, &:active': { transform: 'scale(1.1)' },
            }}
            href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=1098duedates`}
          >
            File Now!
          </Button>
        </Box>
      </Container>
      <Grid
        container
        sx={{
          backgroundColor: '#f0f2f9',
          py: 5,
          mx: '0 !important',
          width: '100% !important',
        }}
        spacing={2}
        m={0}
        mt={0}
      >
        <Container maxWidth="lg" sx={{ mb: 0 }}>
          <DynamicBreadcrumbs />
          <Grid
            container
            spacing={0}
            rowSpacing={0}
            m={0}
            sx={{ flexDirection: 'row-reverse' }}
          >
            <Grid
              size={{ xs: 12, md: 3.5, sm: 4.5 }}
              sx={{ display: { xs: 'none', sm: 'block' } }}
            >
              <ResourcesRightSidebar />
            </Grid>
            <Grid size={{ xs: 12, md: 8.5, sm: 7.5 }}>
              <Paper
                variant="outlined"
                sx={{
                  border: '1px solid #ccc',
                  p: 3,
                  px: 3,
                  textAlign: 'left',
                  mb: 0,
                }}
              >
                <Box
                  itemScope
                  itemType="https://schema.org/Article"
                  sx={{
                    p: 3,
                    px: 3,
                    backgroundColor: '#f0f5f8',
                    borderLeft: '5px solid #01456c',
                    mb: 4,
                    borderRadius: 3,
                    position: 'relative',
                  }}
                >
                  <link
                    itemProp="mainEntityOfPage"
                    href="https://www.taxzerone.com/resources/information-returns/form-1098-series-due-dates/"
                  />

                  {/* Article requires an image */}
                  <div
                    itemProp="image"
                    itemScope
                    itemType="https://schema.org/ImageObject"
                    style={{ display: 'none' }}
                  >
                    <link
                      itemProp="url"
                      href="https://www.taxzerone.com/assets/images/logo.webp"
                    />
                    <data itemProp="width" value="380" />
                    <data itemProp="height" value="124" />
                  </div>

                  <Box
                    sx={{
                      display: 'grid',
                      justifyContent: 'flex-start',
                      alignItems: 'center',
                      textAlign: 'left',
                      mb: 2,
                    }}
                  >
                    <Typography variant="body2">
                      Last updated:{' '}
                      <time
                        itemProp="datePublished"
                        dateTime="2026-07-07T09:00:00-05:00"
                      >
                        July 7, 2026
                      </time>
                      <time
                        itemProp="dateModified"
                        dateTime="2026-07-07T09:00:00-05:00"
                        style={{ display: 'none' }}
                      >
                        July 7, 2026
                      </time>
                    </Typography>

                    <Typography
                      variant="body2"
                      sx={{ fontWeight: 600 }}
                      itemProp="author"
                      itemScope
                      itemType="https://schema.org/Person"
                    >
                      -by{' '}
                      <span itemProp="name">
                        <a
                          itemProp="url"
                          href={`${process.env.NEXT_PUBLIC_HOMESITE_URL}`}
                          style={{ color: 'inherit', textDecoration: 'none' }}
                        >
                          Alexia Zepeda
                        </a>
                      </span>{' '}
                      | TaxZerone
                    </Typography>
                  </Box>

                  <Typography
                    variant="h4"
                    itemProp="headline"
                    sx={{
                      fontWeight: 600,
                      mb: 2,
                      fontSize: { md: 20, sm: 18, xs: 16 },
                      color: '#01456c',
                      fontStyle: 'italic',
                    }}
                  >
                    Form 1098 series due dates at a Glance
                  </Typography>

                  <Box
                    component="ul"
                    itemProp="articleBody"
                    sx={{
                      mb: 0,
                      pl: 0,
                      listStyle: 'none',

                      '& li': {
                        mb: 1.6,
                        position: 'relative',
                        pl: 3,
                      },

                      '& li::before': {
                        content: '"★"',
                        position: 'absolute',
                        left: 0,
                        top: '-2px',
                        color: '#01456c',
                        fontSize: '18px',
                      },
                    }}
                  >
                    <li>
                      <span>
                        <strong style={{ fontWeight: 600 }}>
                          Forms Covered:
                        </strong>
                        {
                          ' Form 1098 (Mortgage Interest Statement), Form 1098-T (Tuition Statement), and Form 1098-E (Student Loan Interest Statement).'
                        }
                      </span>
                    </li>
                    <li>
                      <span>
                        <strong style={{ fontWeight: 600 }}>
                          Filing Deadlines:
                        </strong>
                        {' Paper filings are '}
                        generally due
                        {' by '}
                        <strong style={{ fontWeight: 600 }}>February 28</strong>
                        {', electronic filings by '}
                        <strong style={{ fontWeight: 600 }}>March 31</strong>
                        {', and recipient copies must be '}
                        furnished
                        {' by '}
                        <strong style={{ fontWeight: 600 }}>January 31</strong>.
                      </span>
                    </li>
                    <li>
                      <span>
                        <strong style={{ fontWeight: 600 }}>
                          Who Must File:
                        </strong>
                        {
                          ' Lenders, educational institutions, and student loan interest recipients meeting IRS reporting requirements must file applicable '
                        }
                        <Link
                          href="/form-1098-series/"
                          sx={{ color: '#0202ff' }}
                        >
                          Form 1098 series
                        </Link>
                        {' returns.'}
                      </span>
                    </li>
                    <li>
                      <span>
                        <strong style={{ fontWeight: 600 }}>Penalties:</strong>
                        {
                          ' Late, incorrect, or missing Form 1098 series filings may result in IRS penalties based on the level of noncompliance.'
                        }
                      </span>
                    </li>
                  </Box>

                  <div
                    itemProp="publisher"
                    itemScope
                    itemType="https://schema.org/Organization"
                    style={{ display: 'none' }}
                  >
                    <span itemProp="name">TaxZerone</span>
                    <div
                      itemProp="logo"
                      itemScope
                      itemType="https://schema.org/ImageObject"
                    >
                      <link
                        itemProp="url"
                        href="https://www.taxzerone.com/assets/images/logo.webp"
                      />
                      <data itemProp="width" value="380" />
                      <data itemProp="height" value="124" />
                    </div>
                  </div>
                </Box>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  If any Individual or Entity receive Mortgage Interest, Student
                  Loan Interest, Tuition fees need to file the 1098 forms. It is
                  information return which should be submitted with IRS and
                  Barrower/ Student copies need to be send to the respective
                  individual.
                </Typography>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Types of 1098 forms
                </Typography>
                <Box component="ol" sx={{ mb: 3, '& li': { mb: 1 } }}>
                  <li>Form 1098 </li>
                  <li>Form 1098-T </li>
                  <li>Form 1098-E </li>
                </Box>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 20, md: 18, sm: 16, xs: 16 },
                    mb: 0.5,
                    fontWeight: 600,
                  }}
                >
                  Form 1098
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  {' '}
                  <Link
                    href="/information-returns/efile-irs-form-1098/"
                    sx={{ color: '#0202ff' }}
                  >
                    {' '}
                    Form 1098
                  </Link>
                  , Mortgage Interest Statement is used by the Lender to report
                  the interest received upon the Mortgage. Generally, the lender
                  who receive Mortgage interest of above $600 need to file form
                  1098 with IRS and send the borrower copies to the individual
                  who paid the interest. Learn more about{' '}
                  <Link
                    href="/resources/information-returns/form-1098-instructions/"
                    sx={{ color: '#0202ff' }}
                  >
                    form 1098 and its filing instruction
                  </Link>
                  .
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 20, md: 18, sm: 16, xs: 16 },
                    mb: 0.5,
                    fontWeight: 600,
                  }}
                >
                  Form 1098-T
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  {' '}
                  <Link
                    href="/information-returns/efile-irs-form-1098-t/"
                    sx={{ color: '#0202ff' }}
                  >
                    {' '}
                    Form 1098-T
                  </Link>
                  , Tuition Statement is an IRS form used by Educational
                  Institutions to report the eligible tuition fees and
                  educational spend done by the students. The insurer who
                  refunds or reimburse any of the eligible education expense of
                  the student should also furnish form 1098-T to IRS and the
                  student. Learn more about{' '}
                  <Link
                    href="/resources/information-returns/form-1098-t-instructions/"
                    sx={{ color: '#0202ff' }}
                  >
                    form 1098 and its filing instruction
                  </Link>
                  .
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 20, md: 18, sm: 16, xs: 16 },
                    mb: 0.5,
                    fontWeight: 600,
                  }}
                >
                  Form 1098-E
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  {' '}
                  <Link
                    href="/information-returns/efile-irs-form-1098-e/"
                    sx={{ color: '#0202ff' }}
                  >
                    {' '}
                    Form 1098-E
                  </Link>
                  , Student Loan Interest statement is an IRS form used by
                  Entity or individual to report the interest received from
                  student loan. IRS mandate the reporting of interest received
                  if the interest received is more than $600 per tax year. Learn
                  more about the{' '}
                  <Link
                    href="/resources/information-returns/form-1098-e-instructions/"
                    sx={{ color: '#0202ff' }}
                  >
                    form 1098 and its filing instruction
                  </Link>
                  .
                </Typography>
                <Box
                  component="div"
                  sx={{ mt: 3, mb: 5, p: 2, borderRadius: 2 }}
                  className="gradient-bg"
                >
                  <Stack
                    direction={{ xs: 'column', md: 'row' }}
                    spacing={2}
                    justifyContent="space-between"
                    alignItems="center"
                  >
                    <Box sx={{ width: '100%' }}>
                      <Typography
                        variant="body1"
                        sx={{ fontWeight: 500, mb: 0, fontSize: 15 }}
                      >
                        Now file all your 1098 forms using TaxZerone
                      </Typography>
                    </Box>
                    <Box
                      sx={{ width: '100%', maxWidth: 150, textAlign: 'center' }}
                    >
                      <Button
                        variant="contained"
                        sx={{
                          textTransform: 'none',
                          boxShadow: 'none',
                          borderRadius: 2,
                          backgroundColor: '#fff',
                          color: '#333',
                          '&:hover, &:focus, &:active': {
                            color: '#fff',
                            background: '#172b6b',
                          },
                        }}
                        size="large"
                        href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=1098duedates`}
                      >
                        E-File Now
                      </Button>
                    </Box>
                  </Stack>
                </Box>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Important Dates in filing 1098 forms
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Form 1098, Form 1098-T, Form 1098-E have the same deadline for
                  paper filing, E-Filing and sending Borrower/Student copies. It
                  is important to adhere to the deadline to stay compliant with
                  the IRS regulations.
                </Typography>
                <Box component="div" sx={{ mb: 2 }}>
                  <TableContainer
                    sx={{ border: '1px solid #828282', borderRadius: 2 }}
                  >
                    <Table sx={{}} aria-label="Due Date Chart">
                      <TableHead>
                        <TableRow sx={{ backgroundColor: '#ccc' }}>
                          <TableCell
                            sx={{
                              color: '#444',
                              fontWeight: 600,
                              width: 300,
                              borderRight: '1px solid #828282',
                              borderBottom: '1px solid #828282',
                              textAlign: 'center',
                            }}
                          >
                            Filing Type
                          </TableCell>

                          <TableCell
                            sx={{
                              color: '#444',
                              fontWeight: 600,
                              width: 300,

                              borderBottom: '1px solid #828282',
                              textAlign: 'center',
                            }}
                          >
                            Deadline Date
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        <TableRow>
                          <TableCell
                            sx={{
                              borderRight: '1px solid #828282',
                              backgroundColor: '#fff',
                              textAlign: 'center',
                              borderBottom: '1px solid #828282',
                            }}
                          >
                            Paper filing
                          </TableCell>
                          <TableCell
                            sx={{
                              borderBottom: '1px solid #828282',
                              textAlign: 'center',
                              backgroundColor: '#fff',
                            }}
                          >
                            March 2, 2026
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell
                            sx={{
                              borderRight: '1px solid #828282',
                              backgroundColor: '#fff',
                              textAlign: 'center',
                              borderBottom: '1px solid #828282',
                            }}
                          >
                            E-Filing
                          </TableCell>
                          <TableCell
                            sx={{
                              borderBottom: '1px solid #828282',
                              textAlign: 'center',
                              backgroundColor: '#fff',
                            }}
                          >
                            March 31, 2026
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell
                            sx={{
                              borderRight: '1px solid #828282',
                              backgroundColor: '#fff',
                              textAlign: 'center',
                            }}
                          >
                            Sending Recipient copies
                          </TableCell>
                          <TableCell
                            sx={{
                              textAlign: 'center',
                              backgroundColor: '#fff',
                            }}
                          >
                            February 2, 2026
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>

                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 1,
                    mt: 2,
                  }}
                >
                  Penalties
                </Typography>

                <Typography variant="body1" sx={{ mb: 2 }}>
                  <span style={{ fontWeight: 600 }}> Failure to E-file:</span>{' '}
                  If you need to file more than 10 information returns in a tax
                  year, electronic filing is mandatory. Noncompliance can result
                  in a penalty of $340 per return.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <span style={{ fontWeight: 600 }}> Late Filing Penalty:</span>{' '}
                  The IRS sets a deadline for submitting 1098 Forms. Missing
                  this deadline can result in penalties ranging from $60 per
                  return, with a maximum of $4,098,500 per year. For small
                  businesses, the penalty cap is $1,366,000 annually.
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  <span style={{ fontWeight: 600 }}>
                    {' '}
                    Incorrect Information Penalty:
                  </span>{' '}
                  If 1098 Forms contains errors and is not corrected before the
                  deadline, penalties may apply. These penalties align with
                  those for late filing.
                </Typography>

                <Alert severity="info" icon={false} sx={{ borderRadius: 2 }}>
                  <Accordion
                    defaultExpanded
                    sx={{
                      '&.MuiAccordion-root::before, &.MuiAccordion-root.Mui-expanded::before':
                        {
                          background: 'none !important',
                        },
                      '&.MuiAccordion-root': { background: 'none !important' },
                    }}
                  >
                    <AccordionSummary
                      expandIcon={<ExpandMoreIcon />}
                      aria-controls=""
                      id=""
                      sx={{
                        px: 0,
                        m: 0,
                        justifyContent: 'flex-start',
                        '& .MuiAccordionSummary-content, & .MuiAccordionSummary-content.Mui-expanded':
                          { m: '0 !important', flexGrow: 0 },
                      }}
                    >
                      <Typography
                        variant="body1"
                        sx={{
                          fontWeight: 600,
                          color: '#014361',
                          fontSize: 16,
                        }}
                      >
                        Sources and References
                      </Typography>
                    </AccordionSummary>
                    <AccordionDetails sx={{ p: 0 }}>
                      <Typography
                        component="div"
                        variant="body2"
                        sx={{ mt: 1 }}
                      >
                        The information provided is based on guidelines from the
                        Internal Revenue Service (IRS). <br />
                        For more details, please refer to the IRS{' '}
                        <Link
                          href="https://www.irs.gov/pub/irs-pdf/i1098.pdf"
                          target="_blank"
                          sx={{ color: '#0202ff' }}
                        >
                          instructions of Form 1098
                        </Link>
                        ,{' '}
                        <Link
                          href="https://www.irs.gov/pub/irs-pdf/i1098et.pdf"
                          target="_blank"
                          sx={{ color: '#0202ff' }}
                        >
                          instructions of Form 1098-T
                        </Link>
                        ,{' '}
                        <Link
                          href="https://www.irs.gov/pub/irs-pdf/i1098et.pdf"
                          target="_blank"
                          sx={{ color: '#0202ff' }}
                        >
                          instructions of Form 1098-E
                        </Link>{' '}
                        and{' '}
                        <Link
                          href="https://www.irs.gov/instructions/i1099gi"
                          target="_blank"
                          sx={{ color: '#0202ff' }}
                        >
                          General Instructions of Information Returns
                        </Link>
                        .
                      </Typography>
                    </AccordionDetails>
                  </Accordion>
                </Alert>
              </Paper>
            </Grid>
          </Grid>
        </Container>
      </Grid>

      <Container maxWidth="lg" sx={{ mb: 5, my: { xs: 6, sm: 6, md: 6 } }}>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={{ xs: 2, md: 5 }}
        >
          <Box
            sx={{
              width: '100%',
              textAlign: 'center',
            }}
          >
            <Typography
              variant="h2"
              sx={{
                fontSize: { lg: 34, md: 30, sm: 26, xs: 22 },
                fontWeight: 600,
                mb: 2,
                textAlign: 'center',
                lineHeight: 1.5,
              }}
            >
              File 1098 forms With TaxZerone for just $2.49 per form
            </Typography>
            <Typography
              sx={{
                fontSize: { lg: 20, md: 18, sm: 16, xs: 16 },
                fontWeight: 500,
                color: '#333333',
                mb: 2,
              }}
            >
              File more and save more.
            </Typography>
            <Typography
              variant="body1"
              sx={{ mb: 2, fontSize: { lg: 18, md: 18, sm: 16, xs: 16 } }}
            >
              Filing cost can go down up to <b>$0.59 per</b> form when filing in
              bulk
            </Typography>

            <Box sx={{ textAlign: { xs: 'center', md: 'center' } }}>
              <Button
                variant="contained"
                sx={{
                  mt: 1,
                  transition: 'all 0.3s ease 0s',
                  '&:hover, &:focus, &:active': { transform: 'scale(1.1)' },
                }}
                className="btn-primary"
                href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=1098duedates`}
              >
                File 1098 Forms Now
              </Button>
            </Box>
          </Box>
        </Stack>
      </Container>
    </AppLayout>
  );
};

export default Form1098DueDates;
